const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3600';

/**
 * Get Firebase ID token for API authentication
 * @returns {Promise<string>} - Firebase ID token
 */
const getAuthToken = async () => {
  const { getAuth } = await import('firebase/auth');
  const auth = getAuth();
  const user = auth.currentUser;

  if (!user) {
    throw new Error('No authenticated user found');
  }

  return await user.getIdToken();
};

/**
 * Get users for management with pagination and search
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page
 * @param {string} params.lastUserId - Last user ID for pagination
 * @param {string} params.search - Search term for email/name
 * @returns {Promise<Object>} - Users data with pagination info
 */
export const getUsersForManagement = async (params = {}) => {
  try {
    const token = await getAuthToken();

    const queryParams = new URLSearchParams();
    
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }
    
    if (params.lastUserId) {
      queryParams.append('lastUserId', params.lastUserId);
    }
    
    if (params.search) {
      queryParams.append('search', params.search);
    }

    const url = `${API_URL}/users/manage?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch users');
    }

    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch users');
    }
  } catch (error) {
    console.error('Error fetching users for management:', error);
    throw error.message || 'Failed to fetch users';
  }
};

/**
 * Get single user details (all fields)
 * @param {string} userId - User ID
 * @returns {Promise<Object>} - User details
 */
export const getUserDetails = async (userId) => {
  try {
    const token = await getAuthToken();

    const url = `${API_URL}/users/${userId}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch user details');
    }

    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch user details');
    }
  } catch (error) {
    console.error('Error fetching user details:', error);
    throw error.message || 'Failed to fetch user details';
  }
};

/**
 * Get conversations for a specific user (admin only)
 * @param {string} userId - User ID
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page
 * @param {string} params.startAfter - Cursor for pagination
 * @param {string} params.characterId - Filter by character ID
 * @param {string} params.status - Filter by status ('active', 'archived')
 * @returns {Promise<Object>} - Conversations data
 */
export const getUserConversations = async (userId, params = {}) => {
  try {
    const token = await getAuthToken();

    const queryParams = new URLSearchParams();
    
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }
    
    if (params.startAfter) {
      queryParams.append('startAfter', params.startAfter);
    }
    
    if (params.characterId) {
      queryParams.append('characterId', params.characterId);
    }
    
    if (params.status) {
      queryParams.append('status', params.status);
    }

    const url = `${API_URL}/users/${userId}/conversations?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch user conversations');
    }

    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch user conversations');
    }
  } catch (error) {
    console.error('Error fetching user conversations:', error);
    throw error.message || 'Failed to fetch user conversations';
  }
};

/**
 * Get messages for a specific conversation (admin only)
 * @param {string} userId - User ID
 * @param {string} conversationId - Conversation ID
 * @param {Object} params - Query parameters
 * @param {number} params.limit - Results per page
 * @param {string} params.before - Cursor for pagination (timestamp)
 * @returns {Promise<Object>} - Messages data
 */
export const getConversationMessages = async (userId, conversationId, params = {}) => {
  try {
    const token = await getAuthToken();

    const queryParams = new URLSearchParams();
    
    if (params.limit) {
      queryParams.append('limit', params.limit);
    }
    
    if (params.before) {
      queryParams.append('before', params.before);
    }

    const url = `${API_URL}/users/${userId}/conversations/${conversationId}/messages?${queryParams.toString()}`;
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Failed to fetch conversation messages');
    }

    const data = await response.json();
    
    if (data.success) {
      return data.data;
    } else {
      throw new Error(data.error || 'Failed to fetch conversation messages');
    }
  } catch (error) {
    console.error('Error fetching conversation messages:', error);
    throw error.message || 'Failed to fetch conversation messages';
  }
};
